import {
    ProdcatApiSdk,
    ICoreProduct,
    IItem,
    ItemTypes,
    IProductParams
} from '../../api/ProdcatApiSdk';
import { Product } from '../domain/entities/Product';
import { Sku } from '../domain/entities/Sku';
import { IProductSkuMap } from '../domain/entities/ProductSku';
import { observable, action, computed } from 'mobx';
import { LEGACY_INVENTORY_STATUS, InventoryStatus } from '../constants/InventoryStatuses';
import { IInventoryStatuses } from '../../service-setup/ServiceBusController';
import { root } from 'elc-universal-utils';
import { query, serviceQueries } from 'elc-service-bus';
import { InventoryStatusSortingStrategy } from '../domain/services/InventoryStatusSortingStrategy';
import { IQueryTypeFragments } from '../../api/QueryFragments';

export interface IAnalyticsFilters {
    category: string;
    option: string;
}

export interface ISelection {
    size?: string;
    shade?: string;
}

export interface ILoadByParams {
    productIds?: string[];
    skuIds?: string[];
}

export type ParamsByIdType = 'productIds' | 'skuIds';

export interface IProductRepository {
    prodcatApiSdk: ProdcatApiSdk;
}

interface IStoredAnalyticsProductRequests {
    [key: string]: Product[];
}

export class ProductRepository {
    private prodcatApiSdk: ProdcatApiSdk;

    @observable private queryParams: ISelection;
    @observable private _products: Product[] = [];
    private _analyticsProductRequests: IStoredAnalyticsProductRequests = {};
    private _viewableInventoryStatuses: string[] = [];

    constructor(productRepositoryConfig: IProductRepository) {
        this.prodcatApiSdk = productRepositoryConfig.prodcatApiSdk;
        this.queryParams = {};

        this.initializeInvViewableStatuses();
    }

    /**
     * Loaders
     */

    @action
    public setRouterQueryParams(queryParams: ISelection) {
        this.queryParams = queryParams;
    }

    @computed
    public get routerQueryParams() {
        return this.queryParams;
    }

    // @todo: https://jira.esteeonline.com/browse/SDFEFND-631
    public retrieveProductById = async (
        prodId: string,
        useDebounce: boolean = true,
        queryFragments?: IQueryTypeFragments
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await this.prodcatApiSdk.getProductById(
            prodId,
            useDebounce,
            this._viewableInventoryStatuses,
            queryFragments
        );
        // remove once todo is fixed
        const product: ICoreProduct = response.product || response;
        this.setProduct(product);
    };

    public getProductsByTag = async (tags: string[], queryFragments?: IQueryTypeFragments) => {
        let products: Product[] = [];
        const lowerCaseProductTags = tags.map(this.changeTagNameToLowerCase);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const productTagIds = lowerCaseProductTags.filter((tag: any) => !isNaN(tag));
        const productTagsKeys = productTagIds.length ? productTagIds : lowerCaseProductTags;
        const isFilterById = !!productTagIds.length;
        const coreProducts = await this.prodcatApiSdk.getProductsByTag(
            productTagsKeys,
            isFilterById,
            this._viewableInventoryStatuses,
            queryFragments
        );

        if (coreProducts) {
            products = coreProducts.map(Product.fromJson);
            this.setProducts(products);
        }

        return products;
    };

    @action
    public getProductsByItems = async (
        items: IItem[],
        includeInnerHits: boolean = true,
        queryFragments?: IQueryTypeFragments
    ) => {
        let products: Product[] = [];
        const loadedProducts = await this.prodcatApiSdk.getProductsById(
            items,
            includeInnerHits,
            this._viewableInventoryStatuses,
            queryFragments
        );

        if (loadedProducts) {
            products = this.processLoadedProducts(loadedProducts, items);
        }

        return products;
    };

    // @todo See ServiceBusController.ts
    public getProductsForAnalytics = async (
        productIds: string[] = [],
        productTags: string[] = [],
        statusFilter: string[]
    ): Promise<Product[]> => {
        if (!productIds.length && !productTags.length) {
            return Promise.resolve([]);
        }

        // Dumb cache to prevent duplicate requests, race condition notwithstanding
        const cacheKeyData = JSON.stringify({
            productIds,
            productTags,
            statusFilter: this._viewableInventoryStatuses
        });
        const cacheKey = 'btoa' in root ? root.btoa(cacheKeyData) : cacheKeyData;
        if (cacheKey in this._analyticsProductRequests) {
            return this._analyticsProductRequests[cacheKey];
        }

        let newProducts: Product[] = [];

        if (productIds.length) {
            newProducts = newProducts.concat(
                await this.getProductsByIdsForAnalytics(productIds, statusFilter)
            );
        }

        if (productTags.length) {
            newProducts = newProducts.concat(
                await this.getProductsByTagForAnalytics(productTags, statusFilter)
            );
        }

        const newProductIds = newProducts.map(({ productId }) => productId);

        const products = newProductIds
            .reduce((accum, id) => {
                // Pull the entities from the existing items in the repo..
                const storedProduct = this.getProductById(id) as Product;
                if (!!storedProduct) {
                    accum.push(storedProduct);
                } else {
                    // ..otherwise get them from the new list of products
                    const newProduct = newProducts.find(({ productId }) => productId === id);
                    if (newProduct) {
                        accum.push(newProduct);
                    }
                }

                return accum;
            }, [] as Product[])
            .filter((item) => !!item);
        this._analyticsProductRequests[cacheKey] = products;

        return products;
    };

    // @todo See ServiceBusController.ts
    public getProductsByIdsForAnalytics = async (
        productIds: string[],
        statusFilter: string[]
    ): Promise<Product[]> => {
        if (!productIds.length) {
            return Promise.resolve([]);
        }

        const storedProductIds = this.products.map(({ productId }) => productId);
        const unstoredProductIds = productIds.filter((id) => !storedProductIds.includes(id));
        const newItemsParams = ProdcatApiSdk.createItemParamsFromIds(
            unstoredProductIds,
            ItemTypes.product
        );
        const response = <ICoreProduct[]>(
            await this.prodcatApiSdk.getProductsById(
                newItemsParams,
                true,
                this._viewableInventoryStatuses
            )
        );

        return (response.length && response.map(Product.fromJson)) || [];
    };

    // @todo See ServiceBusController.ts
    public getProductsByTagForAnalytics = async (
        tags: string[],
        statusFilter: string[]
    ): Promise<Product[]> => {
        if (!tags.length) {
            return Promise.resolve([]);
        }

        const lowerCaseProductTags = tags.map(this.changeTagNameToLowerCase);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const productTagIds = lowerCaseProductTags.filter((tag: any) => !isNaN(tag));
        const productTagsKeys = productTagIds.length ? productTagIds : lowerCaseProductTags;
        const isFilterById = !!productTagIds.length;
        const response = <ICoreProduct[]>(
            await this.prodcatApiSdk.getProductsByTag(productTagsKeys, isFilterById)
        );

        return (response.length && response.map(Product.fromJson)) || [];
    };

    private getInventoryStatuses = async (statuses: string[]) =>
        query(serviceQueries.GET_CONFIG, statuses);

    private initializeInvViewableStatuses = () => {
        this.getInventoryStatuses(['viewableInventoryStatuses']).then(
            ({ viewableInventoryStatuses }) => {
                this._viewableInventoryStatuses = viewableInventoryStatuses;
            }
        );
    };

    private filterByExistingIds = (ids: string[] = [], type: ItemTypes) =>
        ids.filter((id: string) => {
            if (type === ItemTypes.product) {
                return !this.getProductById(id);
            } else if (type === ItemTypes.sku) {
                return !this.getProductBySkuId(id);
            }

            return false;
        });

    // @todo Use cache
    private loadProductDataByParams = async (paramsById: ILoadByParams) => {
        const { skuIds, productIds } = paramsById;

        // Sanitizes/dedupes/checks store for existing elements
        const toRequestProductIds = this.filterByExistingIds(productIds, ItemTypes.product);
        const toRequestSkuIds = this.filterByExistingIds(skuIds, ItemTypes.sku);

        if (toRequestSkuIds.length > 0 || toRequestProductIds.length > 0) {
            const items = [
                ...ProdcatApiSdk.createItemParamsFromIds(toRequestSkuIds, ItemTypes.sku),
                ...ProdcatApiSdk.createItemParamsFromIds(toRequestProductIds, ItemTypes.product)
            ];

            const coreProducts: ICoreProduct[] = await this.prodcatApiSdk.getProductsById(
                items,
                true,
                this._viewableInventoryStatuses
            );

            const products = coreProducts.map((product) => Product.fromJson(product));

            this.setProducts(products);
        }
    };

    /**
     * Filters
     */

    public getProductById = (productId: string) =>
        <Product>this.products.find((prod: Product) => prod.productId === productId);

    public getProductBySkuId = (skuId: string) =>
        <Product>(
            this.products.find((prod: Product) => prod.skus.find((sku) => sku.skuId === skuId))
        );

    public getProductsByIds = async (paramIds: {
        productIds: string[];
        skuIds: string[];
    }): Promise<Product[]> => {
        const { productIds, skuIds } = paramIds;
        const itemParams = [
            ...ProdcatApiSdk.createItemParamsFromIds(productIds, ItemTypes.product),
            ...ProdcatApiSdk.createItemParamsFromIds(skuIds, ItemTypes.sku)
        ];

        const products = await this.getProductsByItems(itemParams);

        return products;
    };

    public getProductsByProductIds = async (productIds: string[]): Promise<Product[]> => {
        await this.loadProductDataByParams({ productIds: productIds });

        return this.products.filter(({ productId }) => productIds.includes(productId));
    };

    public getSkuProducts = async (skuIds?: string[]): Promise<IProductSkuMap> => {
        await this.loadProductDataByParams({ skuIds });

        const productSkuMap =
            this.products && <IProductSkuMap>this.products.reduce(this.mapProductSkusById, {});

        if (!!skuIds) {
            Object.keys(productSkuMap).forEach((skuId: string) => {
                if (skuIds.indexOf(skuId) < 0) {
                    delete productSkuMap[skuId];
                }
            });
        }

        return <IProductSkuMap>productSkuMap;
    };

    private forceDefaultSku = (productItem: IProductParams, product?: ICoreProduct) => {
        if (product && productItem.defaultSkuId) {
            const newDefaultSkuId = productItem.defaultSkuId;
            const defaultSku = product.skus.items.find((sku) => sku.is_default_sku);

            if (defaultSku && defaultSku.sku_id !== newDefaultSkuId) {
                defaultSku.is_default_sku = false;
            }

            const forcedDefaultSku = product.skus.items.find(
                (sku) => sku.sku_id === newDefaultSkuId
            );
            forcedDefaultSku && (forcedDefaultSku.is_default_sku = true);
        }
    };

    @action
    private processLoadedProducts = (loadedProducts: ICoreProduct[], items: IItem[]) => {
        const {
            product: productsFromItems
        }: { product: IProductParams[] } = ProdcatApiSdk.splitItemByType(items);

        if (productsFromItems.length > 0) {
            productsFromItems.forEach((item: IProductParams) => {
                const loadedProduct = loadedProducts.find(({ product_id: id }) => id === item.id);
                if (loadedProduct) {
                    this.forceDefaultSku(item, loadedProduct);
                }
            });
        }

        const products = loadedProducts.map(Product.fromJson);
        this.setProducts(products);

        return products;
    };

    /**
     * Accessors
     */

    @computed
    get numProducts() {
        return this.products.length;
    }

    @computed
    get groupTempOutOfStock() {
        return this.removedOutOfStock.filter(
            (product) =>
                product &&
                !!product.skus.every(
                    (sku) => sku.inventoryStatus === InventoryStatus.TempOutOfStock
                )
        );
    }

    @computed
    get groupNoTempOutOfStock() {
        return this.removedOutOfStock.filter(
            (product) =>
                product &&
                !product.skus.every((sku) => sku.inventoryStatus === InventoryStatus.TempOutOfStock)
        );
    }

    @computed
    public get removedOutOfStock() {
        return this.products.filter(
            (product) =>
                product &&
                !!product.skus.some((sku) => sku.inventoryStatus !== InventoryStatus.SoldOut)
        );
    }

    // @todo What value is this?
    @computed
    public get skuIds() {
        return this.products.map((product) => product.skus[0].skuId);
    }

    @computed
    public get products(): Product[] {
        return this._products;
    }

    @action
    public setProducts = (products: Product[]) => {
        const restItems = products.reduce((accumulator: Product[], product: Product) => {
            const existingProduct = this.products.find(
                ({ productId }) => productId === product.productId
            );

            if (existingProduct) {
                existingProduct.mergeSkus(product.skus);
            } else {
                accumulator.push(product);
            }

            return accumulator;
        }, []);

        this._products = this.products.concat(restItems);
    };

    @action
    private setProduct = (item: ICoreProduct) => {
        const { product_id } = item;
        if (!this.getProductById(product_id)) {
            this._products = [...this.products, Product.fromJson(item)];
        } else {
            const productItemIndex = this.products.findIndex(
                (product: Product) => product.productId === product_id
            );
            if (productItemIndex !== -1) {
                this._products[productItemIndex] = Product.fromJson(item);
            }
        }
    };

    /**
     * Helpers
     */

    private mapProductSkusById = (data: IProductSkuMap, product: Product) => {
        const getProductSkuMap = (skuData: IProductSkuMap, sku: Sku) => {
            const { skus, ...productDetails } = product;
            const newSku = {
                ...productDetails,
                ...sku
            };

            return {
                ...skuData,
                [sku.skuId]: newSku
            };
        };
        const skusData = product.skus.reduce(getProductSkuMap, {});

        return { ...data, ...skusData };
    };

    private changeTagNameToLowerCase = (productTag: string) => productTag.toLowerCase();

    private isLegacyStatus = (status: string | number) => !isNaN(Number(status));

    private getInventoryStatusesSorting = async (inventorySorting: string[]) => {
        return query(serviceQueries.GET_CONFIG, inventorySorting);
    };

    private sortProductSkus = async (product: Product) => {
        const config = await this.getInventoryStatusesSorting([
            'inventoryStatusSorting',
            'backorder'
        ]);
        const isBackorderEnabled = config.backorder && config.backorder.enabled;
        const inventoryStatusSorting = config.inventoryStatusSorting;

        if (inventoryStatusSorting && inventoryStatusSorting.enabled) {
            const sortingStrategy = new InventoryStatusSortingStrategy(
                inventoryStatusSorting.statuses || [],
                isBackorderEnabled
            );

            product.sortSkus(sortingStrategy.sortSkusByInventory);
        }
    };

    @action
    public updateInventoryStatus = async (inventory: IInventoryStatuses) => {
        const affectedSkuIds = Object.keys(inventory);
        this._products = this._products.map((product) => {
            product.skus = product.skus.map((sku) => {
                const { skuId } = sku;
                if (affectedSkuIds.includes(skuId)) {
                    let status = inventory[skuId].inventory_status;
                    const highVelocityQuantity = inventory[skuId].highVelocityQuantity;

                    if (this.isLegacyStatus(status) && status in LEGACY_INVENTORY_STATUS) {
                        status = LEGACY_INVENTORY_STATUS[status];
                    }

                    sku.inventoryStatus = <string>status;
                    if (inventory[skuId].isUpdatedInventorySource) {
                        sku.isUpdatedInventorySource = true;
                    }
                    if (highVelocityQuantity) {
                        sku.highVelocityQuantity = highVelocityQuantity;
                    }
                }

                return sku;
            });
            this.sortProductSkus(product);

            return product;
        });
    };

    @action
    public updateFavorites = (favoriteSkuBaseIds: number[], isFavorite: boolean = true) => {
        this._products = this._products.map((product) => {
            product.skus = product.skus.map((sku) => {
                const {
                    perlgem: { SKU_BASE_ID: skuBaseId }
                } = sku;
                if (favoriteSkuBaseIds.includes(skuBaseId)) {
                    sku.isFavorite = isFavorite;
                }

                return sku;
            });

            return product;
        });
    };
}
